/**
 * PostOpOrders.js
 *
 * Structured post-operative orders form. Covers diet, IV fluids, medications,
 * activity level, wound care, monitoring frequency, drain management, and
 * special instructions.
 *
 * Props:
 *   surgeryId - unique identifier for the current surgery
 */
import React, { useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import DeleteIcon from '@material-ui/icons/Delete';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TextField from '../../Utils/FormInputs/TextField';
import SelectInput from '../../Utils/FormInputs/SelectInput';
import Label from '../../Utils/FormInputs/Label';
import { ResizableTextarea } from '../../Utils/FormInputs/Textarea';
import { surgeryService } from '../../../services/surgeryService';

const useStyles = makeStyles(() => ({
  root: {
    padding: '24px 28px',
    borderRadius: 8,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#212121',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: '0.82rem',
    color: '#757575',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
    marginBottom: 14,
    paddingBottom: 8,
    borderBottom: '1px solid #F5F5F5',
  },
  medTableHeader: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#FAFAFA',
    padding: '8px 12px',
    borderRadius: '6px 6px 0 0',
    border: '1px solid #EEEEEE',
    borderBottom: 'none',
    marginTop: 8,
  },
  medHeaderCell: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#9E9E9E',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  medRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    border: '1px solid #EEEEEE',
    borderTop: 'none',
    gap: 8,
    '&:last-child': {
      borderRadius: '0 0 6px 6px',
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 16,
    borderTop: '1px solid #EEEEEE',
    marginTop: 8,
  },
}));

const DIET_OPTIONS = [
  { value: 'NPO', text: 'NPO' },
  { value: 'Clear fluids', text: 'Clear fluids' },
  { value: 'Soft diet', text: 'Soft diet' },
  { value: 'Regular diet', text: 'Regular diet' },
];

const ACTIVITY_OPTIONS = [
  { value: 'Bed rest', text: 'Bed rest' },
  { value: 'Ambulate with assistance', text: 'Ambulate with assistance' },
  { value: 'Ambulate freely', text: 'Ambulate freely' },
];

const VITALS_FREQUENCY_OPTIONS = [
  { value: 'Every 15 minutes', text: 'Every 15 minutes' },
  { value: 'Every 30 minutes', text: 'Every 30 minutes' },
  { value: 'Every 1 hour', text: 'Every 1 hour' },
  { value: 'Every 2 hours', text: 'Every 2 hours' },
  { value: 'Every 4 hours', text: 'Every 4 hours' },
];

const ROUTE_OPTIONS = [
  { value: 'PO', text: 'PO' },
  { value: 'IV', text: 'IV' },
  { value: 'IM', text: 'IM' },
  { value: 'SC', text: 'SC' },
  { value: 'PR', text: 'PR' },
  { value: 'Topical', text: 'Topical' },
];

const FREQUENCY_OPTIONS = [
  { value: 'Once', text: 'Once' },
  { value: 'BD', text: 'BD' },
  { value: 'TDS', text: 'TDS' },
  { value: 'QID', text: 'QID' },
  { value: '6-hourly', text: '6-hourly' },
  { value: '8-hourly', text: '8-hourly' },
  { value: '12-hourly', text: '12-hourly' },
  { value: 'PRN', text: 'PRN' },
  { value: 'Stat', text: 'Stat' },
];

const createEmptyMedRow = () => ({
  id: Date.now() + Math.random(),
  drugName: '',
  dose: '',
  route: 'PO',
  frequency: 'BD',
  duration: '',
});

const PostOpOrders = ({ surgeryId }) => {
  const classes = useStyles();

  // Diet
  const [diet, setDiet] = useState('NPO');
  const [dietStartTime, setDietStartTime] = useState('');

  // IV Fluids
  const [fluidName, setFluidName] = useState('');
  const [fluidRate, setFluidRate] = useState('');
  const [fluidDuration, setFluidDuration] = useState('');

  // Medications
  const [medications, setMedications] = useState([createEmptyMedRow()]);

  // Activity
  const [activity, setActivity] = useState('Bed rest');

  // Wound Care
  const [woundCareInstructions, setWoundCareInstructions] = useState('');

  // Monitoring
  const [vitalsFrequency, setVitalsFrequency] = useState('Every 1 hour');

  // Drain Management
  const [drainType, setDrainType] = useState('');
  const [expectedOutput, setExpectedOutput] = useState('');
  const [drainRemoval, setDrainRemoval] = useState('');

  // Special Instructions
  const [specialInstructions, setSpecialInstructions] = useState('');

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleMedChange = useCallback((index, field, value) => {
    setMedications((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const handleAddMed = () => {
    setMedications((prev) => [...prev, createEmptyMedRow()]);
  };

  const handleDeleteMed = (index) => {
    setMedications((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await surgeryService.createPostOpOrder({
        surgery_id: surgeryId,
        diet: { type: diet, start_time: dietStartTime },
        iv_fluids: { name: fluidName, rate_ml_hr: fluidRate, duration: fluidDuration },
        medications: medications.map((m) => ({
          drug_name: m.drugName,
          dose: m.dose,
          route: m.route,
          frequency: m.frequency,
          duration: m.duration,
        })),
        activity,
        wound_care: woundCareInstructions,
        monitoring: { vitals_frequency: vitalsFrequency },
        drain_management: {
          drain_type: drainType,
          expected_output: expectedOutput,
          removal_criteria: drainRemoval,
        },
        special_instructions: specialInstructions,
        ordered_at: new Date().toISOString(),
      });
      const data = res.data || res;
      showSnackbar(data.message || `Post-op orders saved (Order #${data.order_id || '—'})`);
    } catch (err) {
      showSnackbar(err.message || 'Failed to save post-op orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper className={classes.root} elevation={1}>
      <div className={classes.header}>
        <Typography className={classes.title}>Post-Operative Orders</Typography>
        <Typography className={classes.subtitle}>
          Structured post-op care instructions &mdash; Surgery #{surgeryId}
        </Typography>
      </div>

      {/* Diet */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Diet</Typography>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group>
              <Label name="Diet Type" />
              <SelectInput
                name="diet"
                value={diet}
                onChange={(e) => setDiet(e.target.value)}
                options={DIET_OPTIONS}
                disabled={loading}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Label name="Start Time" />
              <TextField
                name="dietStartTime"
                type="time"
                value={dietStartTime}
                onChange={(e) => setDietStartTime(e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* IV Fluids */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>IV Fluids</Typography>
        <Form.Group>
          <Label name="Fluid Name" />
          <TextField
            name="fluidName"
            type="text"
            value={fluidName}
            onChange={(e) => setFluidName(e.target.value)}
            placeholder="e.g. Normal Saline 0.9%"
            disabled={loading}
          />
        </Form.Group>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group>
              <Label name="Rate (ml/hr)" />
              <TextField
                name="fluidRate"
                type="number"
                value={fluidRate}
                onChange={(e) => setFluidRate(e.target.value)}
                placeholder="e.g. 125"
                disabled={loading}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Label name="Duration" />
              <TextField
                name="fluidDuration"
                type="text"
                value={fluidDuration}
                onChange={(e) => setFluidDuration(e.target.value)}
                placeholder="e.g. 8 hours"
                disabled={loading}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Medications */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Medications</Typography>

        {/* Table Header */}
        <div className={classes.medTableHeader}>
          <div style={{ width: '25%' }}><span className={classes.medHeaderCell}>Drug Name</span></div>
          <div style={{ width: '15%' }}><span className={classes.medHeaderCell}>Dose</span></div>
          <div style={{ width: '15%' }}><span className={classes.medHeaderCell}>Route</span></div>
          <div style={{ width: '15%' }}><span className={classes.medHeaderCell}>Frequency</span></div>
          <div style={{ width: '20%' }}><span className={classes.medHeaderCell}>Duration</span></div>
          <div style={{ width: '10%' }} />
        </div>

        {/* Medication Rows */}
        {medications.map((med, index) => (
          <div className={classes.medRow} key={med.id}>
            <div style={{ width: '25%' }}>
              <TextField
                name={`drugName_${index}`}
                type="text"
                placeholder="e.g. Paracetamol"
                value={med.drugName}
                onChange={(e) => handleMedChange(index, 'drugName', e.target.value)}
                disabled={loading}
              />
            </div>
            <div style={{ width: '15%' }}>
              <TextField
                name={`dose_${index}`}
                type="text"
                placeholder="e.g. 1g"
                value={med.dose}
                onChange={(e) => handleMedChange(index, 'dose', e.target.value)}
                disabled={loading}
              />
            </div>
            <div style={{ width: '15%' }}>
              <SelectInput
                name={`route_${index}`}
                value={med.route}
                onChange={(e) => handleMedChange(index, 'route', e.target.value)}
                options={ROUTE_OPTIONS}
                disabled={loading}
              />
            </div>
            <div style={{ width: '15%' }}>
              <SelectInput
                name={`frequency_${index}`}
                value={med.frequency}
                onChange={(e) => handleMedChange(index, 'frequency', e.target.value)}
                options={FREQUENCY_OPTIONS}
                disabled={loading}
              />
            </div>
            <div style={{ width: '20%' }}>
              <TextField
                name={`duration_${index}`}
                type="text"
                placeholder="e.g. 5 days"
                value={med.duration}
                onChange={(e) => handleMedChange(index, 'duration', e.target.value)}
                disabled={loading}
              />
            </div>
            <div style={{ width: '10%', textAlign: 'center' }}>
              {medications.length > 1 && (
                <IconButton size="small" onClick={() => handleDeleteMed(index)} disabled={loading} title="Remove medication">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          </div>
        ))}

        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn btn-sm sms-gray-btn"
            onClick={handleAddMed}
            disabled={loading}
          >
            + Add Medication
          </button>
        </div>
      </div>

      {/* Activity */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Activity</Typography>
        <Form.Group>
          <Label name="Activity Level" />
          <SelectInput
            name="activity"
            value={activity}
            onChange={(e) => setActivity(e.target.value)}
            options={ACTIVITY_OPTIONS}
            disabled={loading}
          />
        </Form.Group>
      </div>

      {/* Wound Care */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Wound Care</Typography>
        <Form.Group>
          <Label name="Instructions" />
          <ResizableTextarea
            rows={3}
            name="woundCareInstructions"
            value={woundCareInstructions}
            onChange={(e) => setWoundCareInstructions(e.target.value)}
            placeholder="e.g. Keep wound dry for 48 hours. Change dressing daily with normal saline."
            disabled={loading}
          />
        </Form.Group>
      </div>

      {/* Monitoring */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Monitoring</Typography>
        <Form.Group>
          <Label name="Vitals Frequency" />
          <SelectInput
            name="vitalsFrequency"
            value={vitalsFrequency}
            onChange={(e) => setVitalsFrequency(e.target.value)}
            options={VITALS_FREQUENCY_OPTIONS}
            disabled={loading}
          />
        </Form.Group>
      </div>

      {/* Drain Management */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Drain Management</Typography>
        <Form.Group>
          <Label name="Drain Type" />
          <TextField
            name="drainType"
            type="text"
            value={drainType}
            onChange={(e) => setDrainType(e.target.value)}
            placeholder="e.g. Jackson-Pratt, Penrose"
            disabled={loading}
          />
        </Form.Group>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group>
              <Label name="Expected Output" />
              <TextField
                name="expectedOutput"
                type="text"
                value={expectedOutput}
                onChange={(e) => setExpectedOutput(e.target.value)}
                placeholder="e.g. <50ml/day"
                disabled={loading}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Label name="When to Remove" />
              <TextField
                name="drainRemoval"
                type="text"
                value={drainRemoval}
                onChange={(e) => setDrainRemoval(e.target.value)}
                placeholder="e.g. Output <30ml/24hrs"
                disabled={loading}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Special Instructions */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Special Instructions</Typography>
        <Form.Group>
          <ResizableTextarea
            rows={3}
            name="specialInstructions"
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            placeholder="e.g. Elevate affected limb. Monitor urine output hourly."
            disabled={loading}
          />
        </Form.Group>
      </div>

      {/* Footer */}
      <div className={classes.footer}>
        <button
          type="button"
          className="btn btn-sm sms-btn px-4"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Post-Op Orders'}
        </button>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PostOpOrders;
